import React, { useState, useEffect } from 'react';
import { Location, WorkLocation } from '../types';
import { createUserDocument, getAllWorkLocations } from '../firebase/firestore';
import { auth } from '../firebase/config';
import { createUserWithEmailAndPassword, signOut } from 'firebase/auth';

interface AdminCreateUserProps {
  onUserCreated: () => void;
}

const AdminCreateUser: React.FC<AdminCreateUserProps> = ({ onUserCreated }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    selectedLocationId: '',
    pto: '0',
    dateOfBirth: '',
    dateOfHire: ''
  });
  const [workLocations, setWorkLocations] = useState<WorkLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showReauthMessage, setShowReauthMessage] = useState(false);

  useEffect(() => {
    loadWorkLocations();
  }, []);

  const loadWorkLocations = async () => {
    try {
      setLoadingLocations(true);
      const locations = await getAllWorkLocations();
      setWorkLocations(locations);
    } catch (error) {
      console.error('Error loading work locations:', error);
      setError('İş yeri konumları yüklenirken hata oluştu');
    } finally {
      setLoadingLocations(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setShowReauthMessage(false);

    // Validation
    if (!formData.name.trim()) {
      setError('İsim gereklidir');
      return;
    }
    if (!formData.email.trim()) {
      setError('E-posta gereklidir');
      return;
    }
    if (!formData.password.trim()) {
      setError('Şifre gereklidir');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Şifreler eşleşmiyor');
      return;
    }
    if (!formData.selectedLocationId) {
      setError('İş yeri konumu seçilmelidir');
      return;
    }
    if (!formData.pto || parseInt(formData.pto) < 0) {
      setError('Geçerli bir yıllık izin hakkı değeri girin');
      return;
    }
    
    // Validate date of birth if provided
    if (formData.dateOfBirth) {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      if (age < 16 || age > 100) {
        setError('Doğum tarihi 16-100 yaş arasında olmalıdır');
        return;
      }
    }
    
    // Validate date of hire if provided
    if (formData.dateOfHire) {
      const hireDate = new Date(formData.dateOfHire);
      const today = new Date();
      if (hireDate > today) {
        setError('İşe giriş tarihi gelecekte olamaz');
        return;
      }
    }

    setLoading(true);

    try {
      // Find the selected work location
      const selectedLocation = workLocations.find(loc => loc.id === formData.selectedLocationId);
      if (!selectedLocation) {
        throw new Error('Seçilen iş yeri konumu bulunamadı');
      }

      // Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      const newUser = userCredential.user;
      
      // Create work location object from selected location
      const workLocation: Location = {
        latitude: selectedLocation.latitude,
        longitude: selectedLocation.longitude,
        address: selectedLocation.address,
        name: selectedLocation.name
      };

      // Create Firestore user document
      await createUserDocument(newUser.uid, {
        name: formData.name,
        email: formData.email,
        workLocation,
        pto: parseInt(formData.pto),
        dateOfBirth: formData.dateOfBirth || undefined,
        dateOfHire: formData.dateOfHire || undefined
      });

      // Sign out immediately to prevent staying logged in as the new user
      await signOut(auth);

      // Reset form
      setFormData({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        selectedLocationId: '',
        pto: '0',
        dateOfBirth: '',
        dateOfHire: ''
      });

      setSuccess('Kullanıcı başarıyla oluşturuldu!');
      setShowReauthMessage(true);
      onUserCreated(); // Refresh the users list
      
      // Clear success message after 8 seconds
      setTimeout(() => setSuccess(''), 8000);
    } catch (error: any) {
      setError(error.message || 'Kullanıcı oluşturulurken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-2">Yeni Kullanıcı Oluştur</h2>
        <p className="text-sm text-gray-600">Yeni kullanıcı ekleyin ve iş yeri konumunu seçin</p>
        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-800">
            ⚠️ Not: Kullanıcı oluşturulduktan sonra yönetici olarak tekrar giriş yapmanız gerekebilir.
          </p>
        </div>
      </div>

      {showReauthMessage && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Oturum Durumu
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  Kullanıcı başarıyla oluşturuldu! Şu anda oturumunuz kapatıldı. 
                  Yönetici paneline devam etmek için lütfen tekrar giriş yapın.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl">
        {/* User Information */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-md font-medium text-gray-900 mb-4">Kullanıcı Bilgileri</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                İsim *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Kullanıcı adı"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                E-posta *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="kullanici@example.com"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Şifre *
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="En az 6 karakter"
                minLength={6}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Şifre Tekrar *
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Şifreyi tekrar girin"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Yıllık İzin Hakkı *
              </label>
              <input
                type="number"
                name="pto"
                value={formData.pto}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="14"
                min="0"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Doğum Tarihi
              </label>
              <input
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                İşe Giriş Tarihi
              </label>
              <input
                type="date"
                name="dateOfHire"
                value={formData.dateOfHire}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Work Location Selection */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-md font-medium text-gray-900 mb-4">İş Yeri Konumu</h3>
          {loadingLocations ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-sm text-gray-600">Konumlar yükleniyor...</span>
            </div>
          ) : workLocations.length === 0 ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Konum Bulunamadı
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>
                      Henüz iş yeri konumu eklenmemiş. Önce "İş Yeri Konumları" sekmesinden konum ekleyin.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                İş Yeri Konumu Seçin *
              </label>
              <select
                name="selectedLocationId"
                value={formData.selectedLocationId}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Konum seçin...</option>
                {workLocations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name} - {location.address}
                  </option>
                ))}
              </select>
              {formData.selectedLocationId && (
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-blue-800">
                    <strong>Seçilen Konum:</strong> {workLocations.find(loc => loc.id === formData.selectedLocationId)?.name}
                  </p>
                  <p className="text-sm text-blue-600">
                    {workLocations.find(loc => loc.id === formData.selectedLocationId)?.address}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Error and Success Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <p className="text-sm text-green-600">{success}</p>
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading || workLocations.length === 0}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Oluşturuluyor...' : 'Kullanıcı Oluştur'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminCreateUser; 